import React, { useState, useEffect, useCallback, useMemo } from 'react';
import TemplateVariablesField from './TemplateVariablesField';
import { PREVIEW_TEMPLATE_CONTEXT } from '../../utils/messageTemplate';

const ACTIVITY_PRESETS = [
  { id: 'new_7', label: 'Новые (7 дн.)' },
  { id: 'new_30', label: 'Новые (30 дн.)' },
  { id: 'active_30', label: 'Покупали (30 дн.)' },
  { id: 'inactive_30', label: 'Неактивные (30 дн.)' },
  { id: 'inactive_60', label: 'Неактивные (60 дн.)' },
  { id: 'never', label: 'Без покупок' },
];

const BIRTHDAY_MONTHS = [
  { value: '', label: 'Любой' },
  { value: '1', label: 'Январь' },
  { value: '2', label: 'Февраль' },
  { value: '3', label: 'Март' },
  { value: '4', label: 'Апрель' },
  { value: '5', label: 'Май' },
  { value: '6', label: 'Июнь' },
  { value: '7', label: 'Июль' },
  { value: '8', label: 'Август' },
  { value: '9', label: 'Сентябрь' },
  { value: '10', label: 'Октябрь' },
  { value: '11', label: 'Ноябрь' },
  { value: '12', label: 'Декабрь' },
];

const emptySegment = () => ({
  minBalance: '',
  maxBalance: '',
  minSaleAmount: '',
  maxSaleAmount: '',
  activity: '',
  birthdayMonth: '',
});

const parseNum = (v) => {
  if (v === '' || v == null) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

const activityToFilters = (activity) => {
  switch (activity) {
    case 'new_7': return { registeredWithinDays: 7 };
    case 'new_30': return { registeredWithinDays: 30 };
    case 'active_30': return { purchasedWithinDays: 30 };
    case 'inactive_30': return { noPurchaseWithinDays: 30 };
    case 'inactive_60': return { noPurchaseWithinDays: 60 };
    case 'never': return { neverPurchased: true };
    default: return {};
  }
};

const buildApiFilters = (segment) => {
  const filters = {
    minBalance: parseNum(segment.minBalance),
    maxBalance: parseNum(segment.maxBalance),
    minSaleAmount: parseNum(segment.minSaleAmount),
    maxSaleAmount: parseNum(segment.maxSaleAmount),
    birthdayMonth: parseNum(segment.birthdayMonth),
    ...activityToFilters(segment.activity),
  };
  return Object.fromEntries(Object.entries(filters).filter(([, v]) => v != null));
};

const hasSegmentFilters = (segment) => Object.keys(buildApiFilters(segment)).length > 0;

const appendFiltersToSearch = (qs, segment) => {
  const filters = buildApiFilters(segment);
  Object.entries(filters).forEach(([key, value]) => {
    qs.set(key, String(value));
  });
};

const BroadcastTab = ({ contextKey, appUid, templates, user }) => {
  const [message, setMessage] = useState('');
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [segment, setSegment] = useState(emptySegment);
  const [cards, setCards] = useState([]);
  const [selectedCards, setSelectedCards] = useState([]);
  const [sendToAll, setSendToAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState('');
  const [search, setSearch] = useState('');

  const apiFilters = useMemo(() => buildApiFilters(segment), [segment]);
  const segmentActive = useMemo(() => hasSegmentFilters(segment), [segment]);

  const loadCards = useCallback(async () => {
    if (!contextKey || !appUid || !user?.accountId) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams({ contextKey, appUid, accountId: user.accountId });
      if (selectedTemplates.length) {
        qs.set('templateIds', selectedTemplates.join(','));
      }
      appendFiltersToSearch(qs, segment);
      const res = await fetch(`/admin/broadcast/cards?${qs}`);
      const data = await res.json();
      if (!res.ok) {
        setCards([]);
        return;
      }
      setCards(Array.isArray(data) ? data : []);
      setSelectedCards([]);
    } catch {
      setCards([]);
    } finally {
      setLoading(false);
    }
  }, [contextKey, appUid, user?.accountId, selectedTemplates, segment]);

  useEffect(() => { loadCards(); }, [loadCards]);

  const updateSegment = (key, value) => {
    setSegment((prev) => ({ ...prev, [key]: value }));
  };

  const toggleTemplate = (id) => {
    setSelectedTemplates((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    );
  };

  const toggleActivity = (id) => {
    setSegment((prev) => ({
      ...prev,
      activity: prev.activity === id ? '' : id,
    }));
  };

  const clearSegment = () => setSegment(emptySegment());

  const toggleCard = (id) => {
    setSelectedCards((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  const selectAllVisible = () => {
    const ids = filteredCards.map((c) => c._id);
    setSelectedCards(ids);
    setSendToAll(false);
  };

  const recipientCount = useMemo(() => {
    if (sendToAll) return cards.length;
    if (selectedCards.length) return selectedCards.length;
    if (selectedTemplates.length || segmentActive) return cards.length;
    return 0;
  }, [sendToAll, cards.length, selectedCards.length, selectedTemplates.length, segmentActive]);

  const handleSend = async () => {
    if (!message.trim()) {
      alert('Введите текст сообщения');
      return;
    }
    if (!sendToAll && !selectedCards.length && !selectedTemplates.length && !segmentActive) {
      alert('Выберите шаблоны, карты или задайте фильтры сегментации');
      return;
    }

    const count = recipientCount;
    if (!count) {
      alert('Нет получателей по выбранным критериям');
      return;
    }

    if (!window.confirm(`Отправить сообщение ${count} получателям?`)) return;

    setSending(true);
    setResult('');
    try {
      const body = {
        accountId: user.accountId,
        message,
        sendToAll: sendToAll && !selectedCards.length,
        cardIds: sendToAll ? undefined : (selectedCards.length ? selectedCards : undefined),
        templateIds: selectedCards.length ? undefined : (selectedTemplates.length ? selectedTemplates : undefined),
        filters: selectedCards.length ? undefined : (Object.keys(apiFilters).length ? apiFilters : undefined),
      };

      const res = await fetch('/admin/broadcast/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok) {
        setResult(data.message);
        setMessage('');
      } else {
        alert(data.message || 'Ошибка отправки');
      }
    } catch {
      alert('Ошибка отправки');
    } finally {
      setSending(false);
    }
  };

  const filteredCards = cards.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      c.name?.toLowerCase().includes(q) ||
      c.surname?.toLowerCase().includes(q) ||
      c.cardNumber?.includes(q)
    );
  });

  return (
    <div className="admin-section">
      <h2>Рассылка в Telegram</h2>

      <div className="admin-broadcast-layout">
        <div className="admin-broadcast-col">
          <div className="admin-card-group">
            <h3>1. Кому отправить</h3>
            <p className="admin-hint">Шаблоны и фильтры сужают аудиторию. Список карт обновляется автоматически.</p>

            <div className="admin-field">
              <span>Шаблоны</span>
              <div className="admin-day-chips">
                {templates.map((t) => (
                  <button
                    key={t._id}
                    type="button"
                    className={`admin-day-chip ${selectedTemplates.includes(t._id) ? 'active' : ''}`}
                    onClick={() => toggleTemplate(t._id)}
                  >{t.name}</button>
                ))}
              </div>
            </div>

            <div className="admin-broadcast-segment">
              <div className="admin-row-between">
                <span className="admin-segment-title">Сегментация</span>
                {segmentActive && (
                  <button type="button" className="admin-text-btn" onClick={clearSegment}>Сбросить</button>
                )}
              </div>

              <div className="admin-field">
                <span>Активность</span>
                <div className="admin-day-chips">
                  {ACTIVITY_PRESETS.map((preset) => (
                    <button
                      key={preset.id}
                      type="button"
                      className={`admin-day-chip ${segment.activity === preset.id ? 'active' : ''}`}
                      onClick={() => toggleActivity(preset.id)}
                    >{preset.label}</button>
                  ))}
                </div>
              </div>

              <div className="admin-grid-2">
                <label className="admin-field">
                  <span>Баланс от</span>
                  <input
                    type="number"
                    min="0"
                    placeholder="0"
                    value={segment.minBalance}
                    onChange={(e) => updateSegment('minBalance', e.target.value)}
                  />
                </label>
                <label className="admin-field">
                  <span>Баланс до</span>
                  <input
                    type="number"
                    min="0"
                    placeholder="∞"
                    value={segment.maxBalance}
                    onChange={(e) => updateSegment('maxBalance', e.target.value)}
                  />
                </label>
              </div>

              <div className="admin-grid-2">
                <label className="admin-field">
                  <span>Сумма покупок от</span>
                  <input
                    type="number"
                    min="0"
                    placeholder="0"
                    value={segment.minSaleAmount}
                    onChange={(e) => updateSegment('minSaleAmount', e.target.value)}
                  />
                </label>
                <label className="admin-field">
                  <span>Сумма покупок до</span>
                  <input
                    type="number"
                    min="0"
                    placeholder="∞"
                    value={segment.maxSaleAmount}
                    onChange={(e) => updateSegment('maxSaleAmount', e.target.value)}
                  />
                </label>
              </div>

              <label className="admin-field">
                <span>День рождения в месяце</span>
                <select
                  value={segment.birthdayMonth}
                  onChange={(e) => updateSegment('birthdayMonth', e.target.value)}
                >
                  {BIRTHDAY_MONTHS.map((m) => (
                    <option key={m.value || 'any'} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </label>
            </div>

            <label className="admin-field admin-checkbox">
              <input
                type="checkbox"
                checked={sendToAll}
                onChange={(e) => { setSendToAll(e.target.checked); if (e.target.checked) setSelectedCards([]); }}
              />
              <span>Всем из списка ({cards.length})</span>
            </label>
          </div>

          <div className="admin-card-group admin-broadcast-cards-group">
            <div className="admin-row-between">
              <h3>2. Карты</h3>
              <button type="button" className="admin-text-btn" onClick={selectAllVisible}>Выбрать все</button>
            </div>
            <input
              type="text"
              className="admin-search"
              placeholder="Поиск по имени или номеру..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {loading ? (
              <p className="admin-hint">Загрузка...</p>
            ) : (
              <div className="admin-cards-picker">
                {filteredCards.length === 0 && <p className="admin-hint">Нет карт по выбранным критериям</p>}
                {filteredCards.map((card) => (
                  <label key={card._id} className={`admin-card-pick ${selectedCards.includes(card._id) ? 'selected' : ''}`}>
                    <input
                      type="checkbox"
                      checked={selectedCards.includes(card._id)}
                      disabled={sendToAll}
                      onChange={() => toggleCard(card._id)}
                    />
                    <div className="admin-card-pick-info">
                      <strong>{card.name} {card.surname}</strong>
                      <span>#{card.cardNumber} · {card.templateName} · {card.balance} б. · покупок на {card.saleAmount ?? 0}</span>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="admin-broadcast-col">
          <div className="admin-card-group">
            <h3>3. Сообщение</h3>
            <TemplateVariablesField
              label="Текст"
              rows={8}
              placeholder="Привет, {{имя}}! На карте {{баланс}} бонусов. Кэшбэк: {{кэшбэк}}."
              value={message}
              onChange={setMessage}
              previewContext={{
                ...PREVIEW_TEMPLATE_CONTEXT,
                магазин: user?.accountName || PREVIEW_TEMPLATE_CONTEXT.магазин,
              }}
            />
            <p className="admin-hint">
              Получателей: {recipientCount}
              {segmentActive && ' · применены фильтры сегментации'}
            </p>
            {result && <p className="admin-message">{result}</p>}
            <button
              type="button"
              className="admin-save-btn admin-send-btn"
              onClick={handleSend}
              disabled={sending}
            >
              {sending ? 'Отправка...' : 'Отправить в Telegram'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BroadcastTab;
